import "./CustomReports.css"
import { DeleteFilled, DownOutlined } from '@ant-design/icons'
import { Dropdown, Space, message, Empty } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import { selectCurrentId, selectCurrentRole } from "../auth/authSlice"
import { useGetReportTemplatesMutation, useDeleteReportTemplateMutation, useCreateReportMutation } from "./reportApiSlice"
import { useEffect, useState } from "react"
import { showSpinner, hideSpinner } from "../../components/componentSlice"
import { useNavigate } from 'react-router-dom'
import { selectReportTemplates, setReportTemplateList } from "./reportSlice"
import { downloadFile } from "../utils/Utilities"
import SearchableDropdown from "../../components/SearchableDropdown"
import { selectCrm } from "../crm/crmSlice"
import { selectConnectedAdvisors, setSupportStaffAdvisors } from "../crm/support/supportStaffSlice"
import { useGetConnectedAdvisorsMutation } from "../user/userApiSlice"

const CustomReports = () => {
    const id = useSelector(selectCurrentId)
    const advisors = useSelector(selectConnectedAdvisors)
    const role = useSelector(selectCurrentRole)
    const crm = useSelector(selectCrm)
    const reportTemplateList = useSelector(selectReportTemplates)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [messageApi, contextHolder] = message.useMessage()

    const [reportTemplates, setReportTemplates] = useState(null)
    const [errorMessage, setErrorMessage] = useState('')
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [templateSearchValue, setTemplateSearchValue] = useState('')
    const [connectedAdvisors, setConnectedAdvisors] = useState([])
    const [selectedAdvisor, setSelectedAdvisor] = useState(null)

    const [ getConnectedAdvisors,  {
        isLoading: isGetConnectedAdvisorsLoading
    }] = useGetConnectedAdvisorsMutation()
    
    const [ getReportTemplates,  {
            isLoading,
            isError,
            error
     }] = useGetReportTemplatesMutation()

     const [ deleteReportTemplate,  {
        isSuccess: isDeleteSuccess,
        isLoading: isDeleteLoading,
        isError: isDeleteError,
        error: deleteError
    }] = useDeleteReportTemplateMutation()

    const [ createReport,  {
        isSuccess: isCreateReportSuccess,
        isLoading: isCreateReportLoading,
        isError: isCreateReportError,
        error: createReportError
    }] = useCreateReportMutation()

    useEffect(() => {
        if(role === "SUPPORT_STAFF") {
            if(advisors?.length > 0) {
                setConnectedAdvisors(advisors)
                setActiveConnectedAdvisor(advisors)
            } else {
                handleGetConnectedAdvisors()
            }
        } 
    }, [])

    const setActiveConnectedAdvisor = (connectedAdvisors) => {
        if(connectedAdvisors?.length > 0 && reportTemplateList) {
            let activeAdvisor = connectedAdvisors.filter(advisor => advisor.crmDataId === reportTemplateList.crmId)[0]
            setSelectedAdvisor(activeAdvisor)
        }
    }

    const handleGetConnectedAdvisors = async () => {
        const response = await getConnectedAdvisors(id).unwrap()
        if(response) {
            setConnectedAdvisors(response)
            dispatch(setSupportStaffAdvisors(response))

            setActiveConnectedAdvisor(response)
        }
    }

    const successMessage = (message) => {
        messageApi.success(message)
    }

    const infoMessage = (message) => {
        messageApi.info(message)
    }

    useEffect(() => {
        if(isDeleteSuccess) {
            successMessage("Template deleted.")
        }
    }, [isDeleteSuccess])
    
    useEffect(() => {
        async function handleGetReportTemplates() {
            let crmId = role === "ADVISOR" ? crm : selectedAdvisor?.crmDataId
            if(!crmId) {
                return
            }

            try {
                let request = {
                    userId: id,
                    crmId: crmId
                }
                const response = await getReportTemplates(request).unwrap()
                if(response) {
                    dispatch(setReportTemplateList(response))
                    setReportTemplates(response)
                }
            } catch(err) {
                if(!err.status || err.status === "FETCH_ERROR") {
                    setErrorMessage("No Server Response")
                } else {
                    setErrorMessage(err.data?.detail)
                }
            }
        }
        handleGetReportTemplates()
    }, [id, selectedAdvisor])

    useEffect(() => {
        if(reportTemplateList) {
            setReportTemplates(reportTemplateList)
        } 
    }, [reportTemplateList])

    useEffect(() => {
        if(isLoading) {
            dispatch(showSpinner())
        } else{
            dispatch(hideSpinner())
        }
    }, [isLoading])

    useEffect(() => {
        if(isCreateReportLoading) {
            dispatch(showSpinner())
        } else{
            dispatch(hideSpinner())
        }
    }, [isCreateReportLoading])

    const handleMenuClick = (key, fileDetailsId, templateId) => {
        if(key === 'E') {
            navigate(`/dash/customReports/${fileDetailsId}/template/${templateId}`)
        } else if(key === 'D') {
            handleDeleteTemplate(templateId)
        }
    }

    const handleDeleteTemplate = async (templateId) => {
        if(isDeleteLoading) {
            return
        }

        if(!templateId) {
            return
        }

        setSelectedTemplate(null)
        setTemplateSearchValue('')

        infoMessage("Deleting template.")
        let isSuccess = await deleteReportTemplate({id: templateId, userId: id}).unwrap()
        
        if(isSuccess) {
            removeTemplate(templateId)
        }
    }

    const removeTemplate = (templateId) => {
        let updatedTemplates = reportTemplates?.templates?.filter(template => template.id !== templateId)
        let updatedReportTemplates = {...reportTemplates, 
            templates : updatedTemplates
        }

        setReportTemplates(updatedReportTemplates)
        dispatch(setReportTemplateList(updatedReportTemplates))
    }

    const createModificationButton = (fileDetailsId, templateId) => {
        let items = [
            {
                key: 'E',
                label: 'Edit',
            },
            {
                key: 'D',
                danger: true,
                label: 'Delete',
            }
        ]
        return (
            <Dropdown
                menu={{
                items,
                onClick: (e) => handleMenuClick(e.key, fileDetailsId, templateId)
                }}
            >
                <a onClick={(e) => e.preventDefault()}>
                <Space>
                    <DownOutlined />
                </Space>
                </a>
            </Dropdown>
        )
    }

    const handleCreateReport = async () => {
        if(!selectedTemplate) {
            return
        }

        try {
            const data = await createReport({id: selectedTemplate, userId: id}).unwrap()
            console.log(data)
    
            downloadFile(data, templateSearchValue + ".xlsx")
        } catch(err) {
            if(err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response");
            } else if(err.status === 400) {
                setErrorMessage("Error exporting file");
            } else if(err.status === 401) {
                setErrorMessage("Unauthorized");
            } else {
                setErrorMessage(err.data?.message);
            }
        }
    }

    const getAdvisorOptions = () => {
        if(connectedAdvisors) {
            let options = []
            connectedAdvisors.forEach(advisor => {
                options.push({id: advisor, name: advisor.advisorName})
            })

            return options
        }
    }

    const getOptions = () => {
        if(reportTemplates) {
            let options = []
            reportTemplates.templates?.forEach(template => {
                let name = template.templateName + `(${template.templateType})`
                options.push({id: template.id, name})
            })

            return options
        }
    }

    const onChangeSearch = (value, id) => {
        setSelectedTemplate(id)
        setTemplateSearchValue(value)
    }

    const getTable = () => {
        return (
            <div className="file-table-container">
                <div className="file-table-header">
                    <header></header>
                    <div className='file-table-header-field'>
                        <button onClick={() => navigate(`/dash/customReports/${reportTemplates.crmId}/template/new`)}>New</button>
                    </div>
                </div>
                {
                    reportTemplates?.templates && reportTemplates.templates.length > 0 ?
                        <table className="file-table">
                            <tr>
                                <th></th>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Last Updated</th>
                            </tr>
                            {
                                reportTemplates.templates.map(template => {
                                    return (
                                        <tr>
                                            <td>{createModificationButton(reportTemplates.crmId, template.id)}</td>
                                            <td>{template.templateName}</td>
                                            <td>{template.templateType}</td>
                                            <td>{template.lastUpdated}</td>
                                        </tr>
                                    )
                                })
                            }
                        </table>
                    :
                        <table className="file-table">
                            <tr>
                                <td>No template created yet...</td>
                            </tr>
                        </table>        
                }
            </div>
        )
    }

    return (
        <div className="table-page-container">
            {contextHolder}
            {errorMessage && 
                <div className="support-error">
                    <p>{errorMessage}</p>
                </div>
            }
            {role === 'ADVISOR' && !crm ?
                <div className="no-file">
                    <Empty description={"No information yet."} />
                </div>
                :
                <>
                    <div className="custom-report-header">
                        <div className='top-header'>
                            <header>Custom Reports</header>
                        </div>
                    </div>
                    <div className="create-report">
                        {
                            role === "SUPPORT_STAFF" &&
                            <div className="search-dropdown">
                                <SearchableDropdown
                                    options={getAdvisorOptions()}
                                    label="name"
                                    id="id"
                                    selectedVal={selectedAdvisor?.advisorName}
                                    placeholderVal={"Select Advisor..."}
                                    handleChange={(val, id) => setSelectedAdvisor(id)}
                                />
                            </div>
                        }
                        
                        <div className="search-dropdown">
                            <SearchableDropdown
                                options={getOptions()}
                                label="name"
                                id="id"
                                selectedVal={templateSearchValue}
                                placeholderVal={"Select Template..."}
                                handleChange={(val, id) => onChangeSearch(val, id)}
                            />
                        </div>
                        <div className='custom-report-field'>                    
                            <button onClick={() => handleCreateReport()} disabled={!selectedTemplate}>Generate Report</button>
                        </div>
                    </div>
                    {reportTemplates && getTable()}
                </>
            }
        </div>
    )
}

export default CustomReports